import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, CheckCircle, Sparkle, EnvelopeSimple, Warning } from "@phosphor-icons/react";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import BuzzLogo from "../components/BuzzLogo";
import TwilioRequirementCallout from "../components/TwilioRequirementCallout";
import { LiveDot } from "../components/LiveTicker";
import { api } from "../lib/api";

const errText = (err) => {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join("; ");
  if (d && typeof d === "object") return d.msg || JSON.stringify(d);
  return "Signup failed";
};

// Signup during the private beta doubles as a qualification funnel — we're only
// onboarding restaurants that (a) actually need AI phone answering + waitlist
// management, and (b) are willing to give the founder direct feedback. The four
// qualification fields below are captured client-side and posted to
// /api/beta_applications so the founder sees every applicant. The Pydantic
// signup model may drop the extra fields silently — that's fine, we capture
// them via the separate endpoint before the standard signup fires.
// Sourced from REACT_APP_FOUNDER_EMAIL so a founder can swap the public
// contact email without touching code (just change the env var + redeploy).
// Falls back to the beta founder's personal email if the env is missing.
const FOUNDER_EMAIL = process.env.REACT_APP_FOUNDER_EMAIL || "kuchushabupc@gmail.com";

export default function Signup() {
  const { signup } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    // Standard signup
    business_name: "", owner_name: "", email: "", password: "",
    industry: "restaurant", initials: "", accepted_terms: false,
    // Beta qualification
    is_restaurant: "",              // "yes" required to proceed
    missed_calls_per_week: "",      // one of "under_5" / "5_to_15" / "over_15"
    current_waitlist_tool: "",      // "none" / "clipboard" / "waitwhile" / "yelp" / "opentable" / "other"
    wants_feedback_channel: false,  // required — they must commit to giving direct founder feedback
    accepted_beta_terms: false,     // required — acknowledges beta disclaimer
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();

    // Hard-gate the qualification requirements before we even try to create the account.
    if (form.is_restaurant !== "yes") return toast.error("Right now the beta is restaurants-only. Reach out to the founder if you're another kind of business.");
    if (!form.missed_calls_per_week) return toast.error("Tell us roughly how many calls you miss per week.");
    if (!form.current_waitlist_tool) return toast.error("Tell us what you use for your waitlist today.");
    if (!form.wants_feedback_channel) return toast.error("Beta testers must commit to giving direct feedback — please check the box.");
    if (!form.accepted_beta_terms) return toast.error("Please read and acknowledge the beta disclaimer.");
    if (!form.accepted_terms) return toast.error("Please accept the Terms and Privacy Policy.");
    const initials = form.initials.trim().toUpperCase();
    if (initials.length < 2 || initials.length > 5) return toast.error("Enter 2–5 initials to acknowledge the terms");

    setBusy(true);
    try {
      // Fire the beta application first so the founder captures the qualification
      // answers even if the signup step fails downstream. Non-blocking on error.
      try {
        await api.post("/beta_applications", {
          business_name: form.business_name,
          owner_name: form.owner_name,
          email: form.email,
          is_restaurant: form.is_restaurant,
          missed_calls_per_week: form.missed_calls_per_week,
          current_waitlist_tool: form.current_waitlist_tool,
          wants_feedback_channel: form.wants_feedback_channel,
        });
      } catch (_) { /* best-effort — signup proceeds regardless */ }

      await signup({
        business_name: form.business_name,
        owner_name: form.owner_name,
        email: form.email,
        password: form.password,
        industry: form.industry,
        initials,
        accepted_terms: form.accepted_terms,
      });
      nav("/onboard");
      toast.success("Welcome to the BuzzerAI beta — expect a personal email from the founder within 24 hours.");
    } catch (err) { toast.error(errText(err)); }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-[#F7F6F2] grid md:grid-cols-[520px_1fr] relative overflow-hidden font-body">
      {/* LEFT — brand rail with honest beta framing */}
      <div className="hidden md:flex bg-[#0E1512] text-white p-10 flex-col justify-between relative overflow-hidden">
        <div className="absolute -right-40 -top-40 w-[500px] h-[500px] rounded-full bg-[#F5C543]/12 blur-3xl pointer-events-none" />
        <div className="absolute inset-0 hex-bg pointer-events-none" style={{opacity:0.06}} />
        <Link to="/" className="flex items-center gap-2 relative">
          <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center"><BuzzLogo size={22} /></div>
          <span className="font-heading font-extrabold text-xl">BuzzerAI</span>
        </Link>
        <div className="relative">
          <div className="inline-flex items-center gap-1.5 rounded-full bg-[#D96C5B] px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-white mb-4">
            <Sparkle size={11} weight="fill" /> Private Beta · Application only
          </div>
          <h2 className="font-heading text-4xl lg:text-5xl leading-[0.95] tracking-tight">
            Apply to join<br/>
            the <span className="honey-bar italic text-[#F5C543]">first cohort</span>.
          </h2>
          <p className="text-sm text-[#C2D1C1] mt-4 max-w-sm leading-snug">
            We're onboarding a small number of independent restaurants that actually need AI phone answering, smart waitlist management, and a way to turn ringing phones into seated tables. If that's you, apply below — we personally review every application.
          </p>
          <div className="mt-6 space-y-1.5">
            {[
              "Direct founder access · every question answered by Miles",
              "Free during beta · you help us shape the product",
              "Cancel anytime · no card required to apply",
            ].map((s) => (
              <div key={s} className="flex items-center gap-2 text-[11px] font-mono text-[#C2D1C1]">
                <CheckCircle size={12} color="#F5C543" weight="fill" /> {s}
              </div>
            ))}
          </div>
        </div>
        <div className="relative bg-white/5 ring-1 ring-white/10 rounded-xl p-3 text-xs text-[#C2D1C1] leading-snug">
          <div className="flex items-center gap-1.5 text-[10px] tracking-widest uppercase font-bold text-[#F5C543] mb-1">
            <EnvelopeSimple size={12} weight="fill" /> Questions before applying?
          </div>
          Email the founder directly:{" "}
          <a href={`mailto:${FOUNDER_EMAIL}`} className="text-[#F5C543] underline">{FOUNDER_EMAIL}</a>
        </div>
      </div>

      {/* RIGHT — beta application form */}
      <div className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <Link to="/" className="md:hidden flex items-center gap-2 mb-8">
            <div className="w-8 h-8 rounded-lg bg-[#0E1512] flex items-center justify-center"><BuzzLogo size={20} /></div>
            <span className="font-heading font-extrabold">BuzzerAI</span>
          </Link>
          <div className="text-[11px] tracking-[0.28em] uppercase text-[#D96C5B] font-semibold mb-2 flex items-center gap-2">
            <LiveDot color="#D96C5B" /> Beta application
          </div>
          <h1 className="font-heading text-4xl md:text-5xl text-[#0E1512] tracking-tight leading-none mb-3">Tell us about your restaurant.</h1>
          <p className="text-sm text-[#5C6B61] mb-6">Four short qualification questions, then the standard account setup. No credit card required.</p>

          <div className="mb-6"><TwilioRequirementCallout variant="compact" /></div>

          <form onSubmit={submit} className="space-y-3.5" data-testid="signup-form">
            {/* Qualification block */}
            <div className="bg-[#F5C543]/10 border border-[#F5C543]/40 rounded-xl p-4 space-y-4">
              <div className="overline text-[#B45309] flex items-center gap-1">
                <Sparkle size={12} weight="fill" /> Beta qualification
              </div>

              <div>
                <label className="overline block mb-1.5">1. Do you own or manage a restaurant?</label>
                <Select value={form.is_restaurant} onValueChange={(v) => setForm({ ...form, is_restaurant: v })}>
                  <SelectTrigger data-testid="signup-is-restaurant" className="input-field"><SelectValue placeholder="Choose one…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">Yes — I own or manage a restaurant</SelectItem>
                    <SelectItem value="not_yet">Not yet — planning to open one</SelectItem>
                    <SelectItem value="different_business">I run a different kind of business</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="overline block mb-1.5">2. Roughly how many calls does your restaurant miss per week?</label>
                <Select value={form.missed_calls_per_week} onValueChange={(v) => setForm({ ...form, missed_calls_per_week: v })}>
                  <SelectTrigger data-testid="signup-missed-calls" className="input-field"><SelectValue placeholder="Choose one…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="under_5">Fewer than 5</SelectItem>
                    <SelectItem value="5_to_15">5–15</SelectItem>
                    <SelectItem value="15_to_30">15–30</SelectItem>
                    <SelectItem value="over_30">More than 30</SelectItem>
                    <SelectItem value="unsure">Not sure — that's part of the problem</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="overline block mb-1.5">3. What do you use for your waitlist today?</label>
                <Select value={form.current_waitlist_tool} onValueChange={(v) => setForm({ ...form, current_waitlist_tool: v })}>
                  <SelectTrigger data-testid="signup-current-tool" className="input-field"><SelectValue placeholder="Choose one…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nothing — we just tell people to wait</SelectItem>
                    <SelectItem value="clipboard">Pen and paper / clipboard</SelectItem>
                    <SelectItem value="waitwhile">Waitwhile</SelectItem>
                    <SelectItem value="yelp">Yelp Waitlist</SelectItem>
                    <SelectItem value="opentable">OpenTable</SelectItem>
                    <SelectItem value="toast">Toast Waitlist</SelectItem>
                    <SelectItem value="other">Something else</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <label className="flex items-start gap-2 text-xs text-[#121914] leading-snug cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.wants_feedback_channel}
                  onChange={(e) => setForm({ ...form, wants_feedback_channel: e.target.checked })}
                  className="mt-0.5"
                  data-testid="signup-wants-feedback"
                />
                <span>4. I understand this is a private beta and I'm willing to share honest feedback directly with the founder as we improve the product.</span>
              </label>
            </div>

            {/* Standard signup fields */}
            <div>
              <label className="overline block mb-1.5">Restaurant name</label>
              <input data-testid="signup-biz" className="input-field" required value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} />
            </div>
            <div>
              <label className="overline block mb-1.5">Your name</label>
              <input data-testid="signup-owner" className="input-field" required value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} />
            </div>
            <div>
              <label className="overline block mb-1.5">Email</label>
              <input data-testid="signup-email" className="input-field" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div>
              <label className="overline block mb-1.5">Password (8+ chars)</label>
              <input data-testid="signup-password" className="input-field" type="password" required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
            </div>

            {/* Beta disclaimer + terms */}
            <div className="border-t border-black/10 pt-4 space-y-3">
              <div className="bg-[#0E1512] text-white/90 rounded-xl p-4 text-xs leading-relaxed" data-testid="beta-disclaimer">
                <div className="flex items-center gap-2 text-[#F5C543] font-bold uppercase tracking-widest text-[10px] mb-2">
                  <Warning size={12} weight="fill" /> Beta disclaimer — please read
                </div>
                <p>
                  BuzzerAI is currently in <b>private beta</b>. That means the product is stable enough to run your restaurant on, but features may change, occasional bugs may appear, and pricing may adjust before general availability. By joining, you agree to give the founder honest feedback (positive or negative), tolerate the occasional rough edge, and understand that <b>service-level guarantees typical of a mature SaaS do not yet apply</b>. In exchange, you get direct founder access, priority support, free-or-heavily-discounted usage during the beta period, and a seat at the table shaping the product roadmap. For anything unclear before you sign up — pricing, integrations, timeline, whether your specific use case fits — email the founder personally at{" "}
                  <a href={`mailto:${FOUNDER_EMAIL}`} className="text-[#F5C543] underline font-semibold">{FOUNDER_EMAIL}</a>.
                </p>
              </div>

              <label className="flex items-start gap-2 text-xs text-[#5C6B61] leading-snug cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.accepted_beta_terms}
                  onChange={(e) => setForm({ ...form, accepted_beta_terms: e.target.checked })}
                  className="mt-0.5"
                  data-testid="signup-accept-beta"
                />
                <span>I've read the beta disclaimer above and I'm applying knowing this is an early-stage product.</span>
              </label>

              <label className="flex items-start gap-2 text-xs text-[#5C6B61] leading-snug cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.accepted_terms}
                  onChange={(e) => setForm({ ...form, accepted_terms: e.target.checked })}
                  className="mt-0.5"
                  data-testid="signup-accept-terms"
                />
                <span>I have read and agree to the <Link to="/terms" target="_blank" className="underline text-[#1E3A2F]">Terms</Link> and <Link to="/privacy" target="_blank" className="underline text-[#1E3A2F]">Privacy Policy</Link>. My business will obtain valid guest consent before sending SMS.</span>
              </label>

              <div>
                <label className="overline block mb-1.5">Your initials (2–5 letters)</label>
                <input data-testid="signup-initials" className="input-field uppercase" required minLength={2} maxLength={5} placeholder="e.g. JD" value={form.initials} onChange={(e) => setForm({ ...form, initials: e.target.value.toUpperCase() })} />
                <p className="text-[11px] text-[#5C6B61] mt-1 font-mono">Stamped onto your beta application + terms record with today's date.</p>
              </div>
            </div>

            <button data-testid="signup-submit" disabled={busy} className="w-full inline-flex items-center justify-center gap-2 bg-[#0E1512] text-white text-sm font-bold px-6 py-3.5 rounded-full hover:bg-[#1E3A2F] transition-all disabled:opacity-60">
              {busy ? "Submitting…" : "Apply to join the beta"} <ArrowRight size={16} weight="bold" />
            </button>
          </form>

          <p className="mt-6 text-sm text-[#5C6B61]">
            Already applied? <Link to="/login" className="text-[#121914] font-semibold underline decoration-[#F5C543] decoration-2 underline-offset-4">Log in</Link>
          </p>
          <p className="mt-2 text-xs text-[#5C6B61]">
            Questions? Email the founder directly at <a href={`mailto:${FOUNDER_EMAIL}`} className="text-[#0E1512] font-semibold underline">{FOUNDER_EMAIL}</a>.
          </p>
        </div>
      </div>
    </div>
  );
}
